import { Bell, Star, Target, Trophy } from "lucide-react";
import { PageHero, Reveal, SectionHeading } from "@/components/motion";
import { NotifyForm } from "@/components/forms";

const SPORTS = [
  {
    icon: Trophy,
    name: "Football",
    testid: "schedule-card-football",
    detail: "Our post-grad football schedule — kickoff times, opponents and locations — publishes here the moment our 2027 recruiting classes are announced.",
  },
  {
    icon: Target,
    name: "Men's Basketball",
    testid: "schedule-card-mens-bb",
    detail: "The men's basketball slate drops with the class announcement. Expect a high-exposure winter against prep and post-grad competition.",
  },
  {
    icon: Star,
    name: "Women's Basketball",
    testid: "schedule-card-womens-bb",
    detail: "The women's basketball schedule will be released alongside the 2027 class announcement — full season, fully filmed.",
  },
];

const MILESTONES = [
  ["Recruiting classes announced", "Our 2027 classes are locked in and announced publicly."],
  ["Full schedule published", "Football, men's and women's basketball schedules appear on this page."],
  ["Season begins", "Kickoffs and tip-offs — with film from every game flowing to college coaches."],
];

export default function SchedulePage() {
  return (
    <>
      <PageHero
        testid="schedule-hero"
        eyebrow="Inaugural season · 2027"
        lines={[<>The 2027 schedule.</>, <span className="text-ice">Earned, then announced.</span>]}
        sub="The schedules for Football, Men's Basketball and Women's Basketball will be released when the recruiting classes are announced for the 2027 season. We don't publish dates before the roster earns them — get notified the second they drop."
      />

      {/* SPORT STATUS CARDS */}
      <section data-testid="schedule-notice-banner" className="border-t border-white/5 py-24 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid gap-6 md:grid-cols-3">
            {SPORTS.map((s, i) => (
              <Reveal key={s.testid} delay={i * 0.1} className="h-full">
                <div
                  data-testid={s.testid}
                  className="group flex h-full flex-col rounded-2xl border border-white/10 bg-navy-800 p-8 transition-colors hover:border-ice/50"
                >
                  <div className="flex items-center justify-between">
                    <div className="grid h-12 w-12 place-items-center rounded-xl border border-ice/30 bg-ice/10 text-ice">
                      <s.icon className="h-6 w-6" />
                    </div>
                    <span className="flex items-center gap-2 rounded-full border border-crimson/40 bg-crimson/10 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-crimson-bright">
                      <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-crimson-bright" />
                      Schedule pending
                    </span>
                  </div>
                  <h3 className="mt-6 font-display text-2xl font-extrabold uppercase tracking-wide text-white">
                    {s.name}
                  </h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-300">{s.detail}</p>
                  <p className="mt-6 border-t border-white/5 pt-4 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                    Releases with the 2027 class announcement
                  </p>
                </div>
              </Reveal>
            ))}
          </div>

          {/* TIMELINE */}
          <div className="mt-20">
            <SectionHeading
              eyebrow="The sequence"
              title={
                <>
                  How the schedule <span className="text-ice">comes together.</span>
                </>
              }
            />
            <div className="mt-12 grid gap-6 md:grid-cols-3">
              {MILESTONES.map(([t, d], i) => (
                <Reveal key={t} delay={i * 0.1} className="h-full">
                  <div className="h-full rounded-2xl border border-white/10 bg-navy-850 p-6">
                    <span className="font-mono text-sm font-semibold text-crimson-bright">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <h3 className="mt-3 font-display text-xl font-extrabold uppercase tracking-wide text-white">{t}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-300">{d}</p>
                    <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                      Status: waiting
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          {/* NOTIFY */}
          <Reveal delay={0.15} className="mt-20">
            <div className="clip-notch border border-ice/30 bg-ice/5 p-8 sm:p-10">
              <div className="flex items-center gap-3">
                <Bell className="h-5 w-5 text-ice" />
                <p className="font-display text-2xl font-extrabold uppercase text-white">
                  Be first to know
                </p>
              </div>
              <p className="mt-2 max-w-xl text-sm leading-relaxed text-slate-300">
                Drop your email and we'll alert you the moment the 2027 schedules are announced — no spam, just the
                drop.
              </p>
              <div className="mt-6 max-w-xl">
                <NotifyForm interest="2027 Schedule Release" testidPrefix="schedule-notify" cta="Notify Me" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
